import { useRef, useEffect, useState, useMemo, KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useRagChat, ChatMessage } from "../hooks/useRagChat";
import { RagConversation } from "../api/ragApi";
import RagSourceChip from "./RagSourceChip";
import EmptyState from "src/features/statsNew/components/EmptyState";
import { useDictation } from "src/features/tts-and-stt/hooks/useDictation";
import { useConversationMode } from "src/features/tts-and-stt/hooks/useConversationMode";
import MicrophoneButton from "src/features/tts-and-stt/components/MicrophoneButton";
import VoiceModeToggle from "src/features/tts-and-stt/components/VoiceModeToggle";
import SpeakingIndicator from "src/features/tts-and-stt/components/SpeakingIndicator";

interface RagChatProps {
  agentId: number | null;
  hasDocuments: boolean;
  /** Show the built-in chat header. Default true (Agents page). */
  showHeader?: boolean;
  /** Override the "ask a question" empty-state copy. */
  emptyText?: string;
  /**
   * Past thread to open on, for surfaces with history (Wixarpedia). Read once at
   * mount; switch threads by remounting with a new React `key`. Omitted on the
   * Agents page, which is a stateless test bench.
   */
  initialConversation?: RagConversation | null;
  /** Notifies the host that the first question opened a new server-side thread. */
  onConversationStarted?: (conversationId: number) => void;
}

const MessageBubble = ({ message }: { message: ChatMessage }) => {
  const { t } = useTranslation();
  const isUser = message.role === "user";

  const uniqueSources = useMemo(() => {
    if (!message.sources) return [];
    const seen = new Set<string>();
    return message.sources.filter((s) => {
      // Office chunks carry provenance, so two sheets of the same workbook are
      // two distinct sources worth showing. Without metadata the key is just
      // the filename, i.e. the previous behaviour for PDF, TXT and DOCX.
      const key = `${s.filename}|${s.metadata?.sheet ?? ""}|${s.metadata?.slide ?? ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [message.sources]);

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-4`}>
      {!isUser && (
        <div
          className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center mr-2 flex-shrink-0 mt-1"
          aria-hidden="true"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
      )}
      <div
        className={`max-w-[75%] rounded-xl px-4 py-3 text-sm ${
          isUser
            ? "bg-green-600 text-white rounded-br-sm"
            : "bg-gray-50 border border-gray-200 text-gray-800 rounded-bl-sm"
        }`}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap break-words">{message.text}</div>
        ) : (
          <div className="prose prose-sm max-w-none break-words [&>p]:my-2.5 [&>ul]:my-2 [&>ol]:my-2 [&>h1]:text-base [&>h1]:mt-4 [&>h1]:mb-2 [&>h2]:text-sm [&>h2]:mt-3.5 [&>h2]:mb-1.5 [&>h3]:text-sm [&>h3]:mt-3 [&>h3]:mb-1 [&_table]:text-xs [&_table]:w-full [&_table]:border-collapse [&_th]:bg-gray-100 [&_th]:font-semibold [&_th]:text-left [&_th]:px-3 [&_th]:py-2 [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_tr]:border-b [&_tr]:border-gray-200">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                blockquote: ({ children }) => (
                  <div className="my-2 flex items-start gap-2 rounded-r-lg border-l-3 border-green-400 bg-green-50 px-3 py-2 text-gray-600">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <div className="flex-1 [&>p]:my-0">{children}</div>
                  </div>
                ),
              }}
            >
              {message.text}
            </ReactMarkdown>
          </div>
        )}

        {message.error && (
          <div className="mt-2 text-xs text-red-400 flex items-center gap-1" role="alert">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {message.error}
          </div>
        )}

        {uniqueSources.length > 0 && (
          <details className="mt-3 pt-2 border-t border-gray-200/50 group">
            <summary className="text-xs text-gray-500 font-medium cursor-pointer select-none list-none flex items-center gap-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 transition-transform group-open:rotate-90"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              {t("pages.agents.chat.sources")} ({uniqueSources.length})
            </summary>
            <div className="flex flex-wrap gap-1 mt-1">
              {uniqueSources.map((source, idx) => (
                <RagSourceChip key={idx} source={source} />
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

const RagChat = ({
  agentId,
  hasDocuments,
  showHeader = true,
  emptyText,
  initialConversation = null,
  onConversationStarted,
}: RagChatProps) => {
  const { t } = useTranslation();
  const ttsSessionOpenRef = useRef(false);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Refs bridge the cyclic dep between conversation (calls dictation.startListening)
  // and dictation (calls handleSendWithText in the auto-send path).
  const dictationRef = useRef<ReturnType<typeof useDictation> | null>(null);
  const sendWithTextRef = useRef<((text: string) => Promise<void>) | null>(null);

  const conversation = useConversationMode({
    onAutoListenCue: () => {
      dictationRef.current?.startListening().catch(() => undefined);
    },
    onDeactivate: () => {
      dictationRef.current?.stopListening().catch(() => undefined);
    },
  });

  const dictation = useDictation({
    silenceTimeout: conversation.voiceModeEnabled ? 1500 : 30000,
    onTranscript: (text, { viaTimeout }) => {
      if (conversation.voiceModeEnabled && viaTimeout && text.trim()) {
        sendWithTextRef.current?.(text).catch(() => undefined);
      } else {
        setInput(text);
      }
    },
  });
  dictationRef.current = dictation;

  const { messages, isStreaming, sendMessage } = useRagChat(agentId, {
    onChunk: (chunk) => {
      if (ttsSessionOpenRef.current) {
        conversation.pushSpeakingChunk(chunk);
      }
    },
    initialConversation,
    onConversationStarted,
  });

  useEffect(() => {
    if (!dictation.isListening) return;
    const live = [dictation.transcript, dictation.interimTranscript].filter(Boolean).join(" ");
    setInput(live);
  }, [dictation.isListening, dictation.transcript, dictation.interimTranscript]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendWithText = async (text: string) => {
    if (!text.trim() || isStreaming) return;
    const draft = text;
    setInput("");

    if (conversation.voiceModeEnabled) {
      ttsSessionOpenRef.current = true;
      conversation.beginSpeakingSession().catch((err) => {
        console.error("Voice mode session error:", err);
        ttsSessionOpenRef.current = false;
      });
    }

    try {
      await sendMessage(draft);
    } finally {
      if (ttsSessionOpenRef.current) {
        conversation.endSpeakingSession();
        ttsSessionOpenRef.current = false;
      }
    }
  };
  sendWithTextRef.current = handleSendWithText;

  const handleSend = () => {
    // User-initiated commit (Enter / Send): treat as "I'm done — go."
    // Stop the mic, drop accumulated buffer so onEnd won't auto-resend,
    // disarm the pending auto-listen flag, and silence any in-flight TTS.
    if (conversation.voiceModeEnabled) {
      dictation.resetTranscript();
      dictation.stopListening().catch(() => undefined);
      conversation.disarmAutoListen();
      conversation.stopSpeaking();
    }
    return handleSendWithText(input);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!agentId) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Chat header */}
      {showHeader && (
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {t("pages.agents.chat.title")}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">{t("pages.agents.chat.testModeHint")}</p>
        </div>
      )}

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4" aria-live="polite">
        {!hasDocuments && (
          <div className="h-full flex items-center justify-center">
            <EmptyState
              icon="folder"
              title={t("pages.agents.chat.noDocumentsWarning")}
              description={t("pages.agents.chat.noDocumentsHint")}
            />
          </div>
        )}

        {messages.length === 0 && hasDocuments && (
          <div className="h-full flex items-center justify-center">
            <EmptyState icon="search" title={emptyText ?? t("pages.agents.chat.emptyChat")} />
          </div>
        )}

        {messages.map((msg, idx) => {
          // Hide the empty assistant bubble while the thinking indicator is shown
          if (
            isStreaming &&
            idx === messages.length - 1 &&
            msg.role === "assistant" &&
            msg.text === ""
          )
            return null;
          return <MessageBubble key={msg.id} message={msg} />;
        })}

        {isStreaming && messages[messages.length - 1]?.text === "" && (
          <div className="flex justify-start mb-4">
            <div
              className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center mr-2 flex-shrink-0"
              aria-hidden="true"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div
              className="bg-gray-50 border border-gray-200 rounded-xl rounded-bl-sm px-4 py-3 text-sm text-gray-500 flex items-center gap-2"
              role="status"
            >
              <span className="inline-block w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
              {t("pages.agents.chat.thinking")}
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-gray-200 pt-3 px-4 pb-[2px] bg-gray-50 rounded-b-xl">
        <div className="flex items-center justify-between mb-1.5 min-h-[28px]">
          <SpeakingIndicator
            isSpeaking={conversation.isSpeaking}
            onStop={conversation.stopSpeaking}
          />
          <div className="ml-auto mr-20">
            <VoiceModeToggle
              enabled={conversation.voiceModeEnabled}
              onToggle={conversation.toggleVoiceMode}
              disabled={isStreaming}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("pages.agents.chat.placeholder")}
            disabled={isStreaming || !hasDocuments}
            readOnly={dictation.isListening}
            rows={1}
            aria-label={t("pages.agents.chat.placeholder")}
            className={`flex-1 resize-none rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-1 disabled:bg-gray-100 disabled:text-gray-400 transition-all ${
              dictation.isListening
                ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-green-500 focus:ring-green-500"
            }`}
          />
          <MicrophoneButton
            isListening={dictation.isListening}
            isSupported={dictation.isSupported}
            disabled={isStreaming || !hasDocuments}
            idleTooltipKey={
              conversation.voiceModeEnabled
                ? "voice.conversationStartTooltip"
                : "voice.dictateTooltip"
            }
            listeningTooltipKey={
              conversation.voiceModeEnabled
                ? "voice.conversationListeningTooltip"
                : "voice.dictateListeningTooltip"
            }
            cooldown={
              conversation.voiceModeEnabled
                ? { triggerAt: dictation.lastActivityAt, durationMs: 1500 }
                : undefined
            }
            onStartListening={() => {
              if (conversation.voiceModeEnabled && conversation.isSpeaking) {
                conversation.stopSpeaking();
              }
              dictation.resetTranscript();
              dictation.startListening().catch((err) => console.error("STT start error:", err));
            }}
            onStopListening={() => {
              conversation.disarmAutoListen();
              dictation.stopListening().catch(() => undefined);
            }}
          />
          <button
            onClick={handleSend}
            disabled={isStreaming || !input.trim() || !hasDocuments}
            aria-label={t("pages.agents.chat.send")}
            className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:hover:bg-green-600 rounded-xl transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
              />
            </svg>
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5 text-right">
          {t("pages.agents.chat.shiftEnterHint")}
        </p>
      </div>
    </div>
  );
};

export default RagChat;
